import * as React from 'react';
import { ChangeEvent } from 'react';
import './CameraVehicle.scss';
import vehicleImage2 from '../../assets/imgs/vehicle.svg';
import DynamicButton from '../common/DynamicButton';
import Modal from '../Modal';
import {
  MediaId,
  ValidationError,
  PHOTO_DEFINITIONS,
  PhotoSectionId,
  FILTER_PHOTO_SECTIONS,
} from '../../context/photoConstants';
import { Photo, DispatchFunction, IPreInspectionModel } from '../../types';
import { ActionType } from '../../context/actions';
import { deletePhotoFromServer } from '../../helpers/photoUploader';
import { getHelpModalContents } from '../../helpers/photoHelper';
import { BRAND } from '../../static/constants/brands/brand';
import { Company } from '../../interfaces/brand';
import getDeviceBreakpoints from '../../utils/deviceBreakpoints';

interface CameraVehicleProps {
  onCapture?: (mediaId: MediaId) => void; // Se ejecuta después de cerrar el modal (para cámara)
  onChange?: (mediaId: MediaId, event: ChangeEvent<HTMLInputElement>) => void; // Para input file
  onPreview?: (photo: Photo) => void;
  photos?: Photo[];
  dispatch: DispatchFunction;
  setValidationError: (error: ValidationError | null) => void;
  // Estados de loading para cada zona
  loadingStates?: {
    [MediaId.VEHICLE_EXTERIOR_FRONT]?: boolean;
    [MediaId.VEHICLE_EXTERIOR_BACK]?: boolean;
    [MediaId.VEHICLE_EXTERIOR_LEFT]?: boolean;
    [MediaId.VEHICLE_EXTERIOR_RIGHT]?: boolean;
  };
  // Props adicionales para manejar cámara vs input file
  setIsCameraActive?: (active: boolean) => void;
  setPhotoID?: (mediaId: MediaId) => void;
  link?: string;
  noPermissionsLocation?: boolean;
  // Props para determinar qué botones mostrar según configuración
  prePlanType?: string;
  model?: IPreInspectionModel;
}

const CameraVehicle: React.FC<CameraVehicleProps> = ({
  onCapture,
  onChange,
  onPreview,
  dispatch,
  setValidationError,
  loadingStates = {},
  photos,
  setPhotoID,
  link,
  prePlanType = '',
  model,
}) => {
  // Estado para el modal de ayuda
  const [helpModalId, setHelpModalId] = React.useState<MediaId | null>(null);

  // Función para mostrar el modal de ayuda
  const showHelpModal = React.useCallback((mediaId: MediaId) => {
    setHelpModalId(mediaId);
  }, []);

  // Función para cerrar el modal y ejecutar la acción correspondiente (input file o cámara)
  const clearHelpId = React.useCallback(
    (photoId: string) => {
      setHelpModalId(null);

      // Pequeño delay para que el modal se cierre y el DOM se actualice
      setTimeout(() => {
        const mediaId = photoId as MediaId;

        // Establecer photoID si está disponible
        setPhotoID?.(mediaId);

        // Lógica de decisión: input file vs cámara
        const resultEnv = getDeviceBreakpoints();

        // Desktop: siempre usar input file
        if (resultEnv.isDesktop) {
          document.getElementById(`photo-${photoId}`)?.click();
          return;
        }

        // Mobile: lógica condicional
        const shouldUseInputFile = [
          Company.LAFISE.toString(),
          Company.IS.toString(),
        ].includes(BRAND);

        if (shouldUseInputFile) {
          // Usar input file en mobile
          document.getElementById(`photo-${photoId}`)?.click();
        } else {
          // Usar cámara en mobile (excepto si link === 'initial')
          if (link !== 'initial') {
            // Ejecutar onCapture que manejará las validaciones y activará la cámara
            onCapture?.(mediaId);
          }
        }
      }, 150);
    },
    [onCapture, setPhotoID, link],
  );

  // Obtener contenido del modal
  const helpContents = React.useMemo(() => {
    return getHelpModalContents(helpModalId);
  }, [helpModalId]);

  // Mapeo de MediaId a posición en el vehículo (solo para MediaIds que se muestran en el componente CameraVehicle)
  // Nota: VEHICLE_EXTERIOR_PLATE_CR pertenece a la sección OTHERS, no a EXTERIOR
  const mediaIdToPosition: Partial<
    Record<
      MediaId,
      { className: string; position: 'top' | 'left' | 'right' | 'bottom' }
    >
  > = {
    [MediaId.VEHICLE_EXTERIOR_FRONT]: { className: 'btn top', position: 'top' },
    [MediaId.VEHICLE_EXTERIOR_LEFT]: {
      className: 'btn left',
      position: 'left',
    },
    [MediaId.VEHICLE_EXTERIOR_RIGHT]: {
      className: 'btn right',
      position: 'right',
    },
    [MediaId.VEHICLE_EXTERIOR_BACK]: {
      className: 'btn bottom',
      position: 'bottom',
    },
  };

  // Obtener los photoIds válidos según la configuración
  const validPhotoIds = React.useMemo(() => {
    const photoSection = FILTER_PHOTO_SECTIONS(
      PhotoSectionId.EXTERIOR,
      prePlanType || model?.prePlanType || '',
    );
    return photoSection.photoIds;
  }, [prePlanType, model]);

  // Configuración de botones del vehículo basada en la configuración (memoizada)
  const vehicleButtons = React.useMemo(() => {
    return validPhotoIds
      .filter((mediaId) => mediaIdToPosition[mediaId]) // Solo incluir MediaIds que tienen posición definida
      .map((mediaId) => {
        const positionConfig = mediaIdToPosition[mediaId]!; // Safe porque ya filtramos
        return {
          mediaId,
          ...positionConfig,
        };
      });
  }, [validPhotoIds]);

  // Memoización de funciones para evitar recrearlas en cada render
  const hasPhotoWithUrl = React.useCallback(
    (mediaId: MediaId): boolean => {
      if (!photos) return false;
      return photos.some(
        (photo) => photo.id === mediaId && photo.url && photo.url.trim() !== '',
      );
    },
    [photos],
  );

  const hasPhotoError = React.useCallback(
    (mediaId: MediaId): boolean => {
      if (!photos) return false;
      const photo = photos.find((photo) => photo.id === mediaId);
      return !!(
        photo &&
        photo.error &&
        photo.error !== ValidationError.HAS_DAMAGES
      );
    },
    [photos],
  );

  // Memoización de funciones para evitar recrearlas en cada render
  const handleErrorClick = React.useCallback(
    async (photo: Photo) => {
      setValidationError(photo.error || null);
      // Restablece la URL de la imagen después de completar el proceso
      dispatch({
        type: ActionType.UPDATE_PHOTO_ERROR,
        payload: {
          id: photo.id,
          loading: false,
          error: undefined,
        },
      });
      dispatch({
        type: ActionType.UPDATE_PHOTO_URL,
        payload: {
          id: photo.id,
          uniqueId: photo.uniqueId,
          url: undefined,
        },
      });

      await deletePhotoFromServer(photo.url ?? '');
    },
    [dispatch, setValidationError],
  );

  const getButtonType = React.useCallback(
    (mediaId: MediaId): 'camera' | 'loading' | 'view' | 'info' => {
      if (loadingStates[mediaId]) {
        return 'loading';
      }
      if (hasPhotoError(mediaId)) {
        return 'info'; // Mostrar icono de info para errores
      }
      if (hasPhotoWithUrl(mediaId)) {
        return 'view';
      }
      return 'camera';
    },
    [loadingStates, hasPhotoError, hasPhotoWithUrl],
  );

  const getPhotoByMediaId = React.useCallback(
    (mediaId: MediaId): Photo | null => {
      if (!photos) return null;
      return photos.find((photo) => photo.id === mediaId) || null;
    },
    [photos],
  );

  const handleButtonClick = React.useCallback(
    (mediaId: MediaId) => {
      const photo = getPhotoByMediaId(mediaId);

      // Si hay error, mostrar información del error (reutilizando lógica existente)
      if (photo && photo.error && photo.error !== ValidationError.HAS_DAMAGES) {
        handleErrorClick(photo);
      }
      // Si existe foto con URL sin error, mostrar preview
      else if (photo && photo.url && photo.url.trim() !== '') {
        onPreview?.(photo);
      } else {
        // Si no existe foto, mostrar modal de ayuda interno
        showHelpModal(mediaId);
      }
    },
    [getPhotoByMediaId, handleErrorClick, onPreview, showHelpModal],
  );

  // Constantes para configuración de botones
  const BUTTON_CONFIG = {
    size: 'small' as const,
    loadingIconSize: '40px',
    normalIconSize: '24px',
  } as const;

  // Memoización del componente VehicleButton para evitar re-renders innecesarios
  const VehicleButton = React.memo<{ button: (typeof vehicleButtons)[0] }>(
    ({ button }) => {
      const isLoading = loadingStates[button.mediaId];
      const buttonType = getButtonType(button.mediaId);

      // Memoización del handler para evitar recrear la función en cada render
      const handleClick = React.useCallback(() => {
        handleButtonClick(button.mediaId);
      }, [button.mediaId]);

      return (
        <DynamicButton
          className={button.className}
          onClick={handleClick}
          type={buttonType}
          size={BUTTON_CONFIG.size}
          iconSize={
            isLoading
              ? BUTTON_CONFIG.loadingIconSize
              : BUTTON_CONFIG.normalIconSize
          }
          loading={isLoading}
          disabled={isLoading}
        />
      );
    },
  );

  // Separar botones por posición para mantener el layout (memoizado)
  const buttonGroups = React.useMemo(
    () => ({
      top: vehicleButtons.filter((btn) => btn.position === 'top'),
      left: vehicleButtons.filter((btn) => btn.position === 'left'),
      right: vehicleButtons.filter((btn) => btn.position === 'right'),
      bottom: vehicleButtons.filter((btn) => btn.position === 'bottom'),
    }),
    [vehicleButtons],
  );

  return (
    <div className="camera-vehicle">
      {/* Botón superior */}
      {buttonGroups.top.map((button) => (
        <VehicleButton key={button.mediaId} button={button} />
      ))}

      {/* Contenedor central con imagen y botones laterales */}
      <div className="middle-row">
        {/* Botón izquierdo */}
        {buttonGroups.left.map((button) => (
          <VehicleButton key={button.mediaId} button={button} />
        ))}

        {/* Imagen del vehículo en el centro */}
        <div className="vehicle-container">
          <img src={vehicleImage2} alt="Vehículo" className="vehicle-image" />
        </div>

        {/* Botón derecho */}
        {buttonGroups.right.map((button) => (
          <VehicleButton key={button.mediaId} button={button} />
        ))}
      </div>

      {/* Botón inferior */}
      {buttonGroups.bottom.map((button) => (
        <VehicleButton key={button.mediaId} button={button} />
      ))}

      {/* Inputs file ocultos para cada zona del vehículo */}
      {vehicleButtons.map((button) => {
        const photoDefinition = PHOTO_DEFINITIONS[button.mediaId];
        const htmlId = `photo-${button.mediaId}`;

        return photoDefinition?.useGallery ? (
          <input
            key={htmlId}
            id={htmlId}
            type="file"
            accept="image/*,video/*"
            onChange={(e) => onChange?.(button.mediaId, e)}
            style={{ display: 'none' }}
          />
        ) : (
          <input
            key={htmlId}
            id={htmlId}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => onChange?.(button.mediaId, e)}
            style={{ display: 'none' }}
          />
        );
      })}

      {/* Modal de ayuda */}
      {helpModalId && helpContents && (
        <Modal
          onBackdropClick={() => clearHelpId(helpModalId)}
          className="photo-help-modal"
        >
          <h2>{helpContents.title}</h2>
          {helpContents.imgUrl && (
            <img
              src={helpContents?.imgUrl}
              alt={helpContents.title}
              height={200}
            />
          )}
          {helpContents.paragraph}
          <button
            onClick={() => clearHelpId(helpModalId)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '10px',
            }}
          >
            Aceptar
          </button>
        </Modal>
      )}
    </div>
  );
};

export default CameraVehicle;
